import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { IAssignment } from '@/components/staffer/Grading/types';
import { ISubmission } from '@/components/interfaces';
import { useQuery } from '@tanstack/react-query';
import Axios from 'axios';
import * as Routes from '@/modules/routes';
import useGradingStore from '@/components/staffer/Grading/useGradingStore';
import useFlags from '@/hooks/useFlags';
import { pollWhileGenerating } from '@/components/staffer/Grading/AISuggestion/aiSuggestion';
import {
  multipleChoiceQuestionAnswerAttributes,
} from '@/components/staffer/Grading/StudentAnswers/MultipleChoiceQuestionStudentAnswer';
import {
  imcAnswerAttributes,
} from '@/components/staffer/Grading/StudentAnswers/ImageMultipleChoiceQuestionStudentAnswer';
import { flattenDeepArray } from '@/modules/TCIUtils';

interface IGradingHooksTypes {
  programId: number;
  studentViewAssessmentResults?: boolean;
  userId: number;
  userType: string;
}

const GradingHooks = ({
  programId, studentViewAssessmentResults = false, userId, userType,
} : IGradingHooksTypes) => {
  const [assignment, setAssignment] = useState<IAssignment>();
  const [submission, setSubmission] = useState<ISubmission>();
  const { assignmentId, studentId } = useParams();
  const { aiGradingActive } = useFlags();

  // ASSIGNMENT
  const assignmentQuery = useQuery(
    ['assignment'],
    () => (
      Axios.get<{ assignment: IAssignment }>(
        Routes.plato_api_assignment_path(
          assignmentId,
          {
            extra_attributes: [
              'assignment_details',
              'assigned_model_types',
              'classrooms',
              'grouped_questions',
              'investigation',
            ],
            only: [
              'assignment_location',
              'due_datetime',
              'id',
              'max_points',
              'normalization_factor',
              'show_answers_to_students',
              'scoring_mode',
            ],
          },
        ),
      ).then(res => res.data)
    ),
  );

  useEffect(() => {
    if (assignmentQuery.isLoading) return;
    const formattedAssignment = assignmentQuery.data.assignment;
    const formattedQuestions = Object.values(assignmentQuery.data.assignment.grouped_questions || {});
    formattedAssignment.questions = formattedQuestions.length > 0 ? flattenDeepArray(formattedQuestions) : [];
    formattedAssignment.questions = formattedAssignment
      .questions
      .sort((a, b) => (a.display_position <= b.display_position ? -1 : 1));

    setAssignment(formattedAssignment);
  }, [assignmentQuery.data]);

  // SUBMISSION
  const submissionQuery = useQuery(['submission', assignmentId, studentId], () => (
    Axios.get(Routes.plato_api_get_student_submission_assignment_path(assignment.id, { student_id: studentId }))
      .then(res => res.data)
  ), { enabled: !!assignment });

  useEffect(() => {
    if (submissionQuery.isLoading) return;

    setSubmission(submissionQuery.data.submission);
  }, [submissionQuery.data, assignmentId, studentId, submissionQuery.isFetching]);

  // QUESTION ANSWER
  const studentQuestionAnswerQuery = useQuery(
    ['studentAnswer', submission?.user_id],
    () => (
      Axios.get(Routes.plato_api_question_answers_path({
        extra_attributes: [
          'grading_teacher',
          ...multipleChoiceQuestionAnswerAttributes,
          ...imcAnswerAttributes,
          ...(aiGradingActive ? ['ai_suggestion'] : []),
        ],
        for_question_ids: assignment.questions.map(q => q.id),
        for_student: submission.user_id,
        includes: aiGradingActive ? ['ai_grading_suggestion'] : undefined,
      }))
        .then(res => res.data)
    ),
    {
      enabled: (!!submission && !!assignment && assignment.questions.length > 0),
      // provide empty array when assignment is loaded and has no questions to prevent infinite loading
      placeholderData: (assignment && assignment.questions.length) === 0 ? [] : undefined,
      refetchInterval: pollWhileGenerating,
    },
  );

  const currentUserQuery = useQuery(
    ['getUser'],
    () => {
      let userPath : string;

      if (userType === 'Student') {
        userPath = Routes.plato_api_student_path(userId);
      } else {
        userPath = Routes.plato_api_staffer_path(userId);
      }

      return Axios.get(userPath).then(res => res.data);
    },
  );

  // AIDEV-NOTE: Gate downstream-query checks on submissionQuery.data rather than the local
  // `submission` useState, which lags by a render because it's set via a useEffect.
  // Without this, there's a render where submissionQuery has resolved, local `submission` is
  // still null, so the `!!submission && ...isLoading` terms are false, isLoading flips false,
  // and the store-writing effect writes `questionAnswers: []` before the QA query has run —
  // causing every card to flash "No answer provided." until the next cycle.
  const hasSubmissionData = !!submissionQuery.data?.submission;
  const isLoading = assignmentQuery.isFetching
    || submissionQuery.isFetching
    || studentQuestionAnswerQuery.isFetching
    || (hasSubmissionData && currentUserQuery.isFetching)
    || submissionQuery.isLoading
    || assignmentQuery.isLoading
    || (hasSubmissionData && studentQuestionAnswerQuery.isLoading)
    || currentUserQuery.isLoading;

  const { setGradingStore } = useGradingStore();

  // AIDEV-NOTE: PLAT-17195 — When the teacher navigates to a new student, `studentId` (URL) updates
  // immediately but `submission` state lags behind its query. Without the guard below, `isLoading`
  // briefly flips true→false between submissionQuery resolving and setSubmission taking effect,
  // and the effect writes a stale mix into the store (new studentId, previous student's submission
  // and question answers). Only sync when `submission` actually matches the URL student.
  useEffect(() => {
    if (isLoading) return;
    if (submission && submission.user_id.toString() !== studentId.toString()) return;

    const student = assignment
      .classrooms.map(cl => cl.students).flat()
      .find(st => st.id.toString() === studentId.toString());

    setGradingStore({
      assignment,
      currentUser: currentUserQuery.data.staffer || currentUserQuery.data.student,
      isLoading,
      isQuestionMode: false,
      isTeacher: ['Admin', 'Coordinator', 'Teacher'].includes(currentUserQuery.data.staffer?.role),
      programId,
      questionAnswers: studentQuestionAnswerQuery.data?.data || [],
      student,
      studentViewAssessmentResults,
      submission,
      userType,
    });
  }, [
    assignment,
    currentUserQuery.data,
    isLoading,
    studentId,
    studentQuestionAnswerQuery.data,
    submission,
  ]);
};

export default GradingHooks;
